import type { Dayjs } from 'dayjs';
import { Calendar } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { DateRange as CalendarDateRange } from 'react-day-picker';

import { buttonVariants } from '@/components/shadcn/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/ui/popover';
import {
  clampDayjsToDateRangeBounds,
  DateRangePickerPanel,
  getCalendarDateKey,
} from '@/shared/ui/date-range-picker';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import { getSelectedPeriodDateRange1x } from '../../lib';
import {
  MAX_SIGNAL_CUSTOM_RANGE_DAYS,
  SIGNAL_DATE_PRESET,
  type SignalDateSelection,
  type SignalDateSelectionInput,
} from '../../model';

export const DISPLAY_DURATION = [
  SIGNAL_DATE_PRESET.PAST_24_HOURS,
  SIGNAL_DATE_PRESET.PAST_3_DAYS,
  SIGNAL_DATE_PRESET.PAST_7_DAYS,
  SIGNAL_DATE_PRESET.PAST_30_DAYS,
  SIGNAL_DATE_PRESET.PAST_90_DAYS,
];

export const getSignalDurationDisplayRange = (
  selection: SignalDateSelectionInput,
) => {
  const [start, end] = getSelectedPeriodDateRange1x(selection);
  return {
    startDate: getTimezoneDayjs(start.toJSON()).format(DATE_FORMAT.readable),
    endDate: getTimezoneDayjs(end.toJSON()).format(DATE_FORMAT.readable),
  };
};

const toCustomSelection = (from: Dayjs, to: Dayjs): SignalDateSelection => ({
  type: 'custom',
  startDate: from.startOf('day').toJSON(),
  endDate: to.endOf('day').toJSON(),
});

export const SignalDurationSelector = ({
  value,
  onChange,
}: {
  value: SignalDateSelection;
  onChange: (selection: SignalDateSelection) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [tempSelection, setTempSelection] = useState(value);
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 960 : false,
  );
  const maxDate = getTimezoneDayjs().endOf('day');
  const dateRange = useMemo(() => getSignalDurationDisplayRange(value), [value]);
  const computedDateRange = useMemo(() => {
    const [from, to] = getSelectedPeriodDateRange1x(tempSelection);
    return { from, to };
  }, [tempSelection]);
  const rangeDays =
    computedDateRange.to
      .startOf('day')
      .diff(computedDateRange.from.startOf('day'), 'day') + 1;
  const validationMessage =
    rangeDays > MAX_SIGNAL_CUSTOM_RANGE_DAYS
      ? `Select a range of ${MAX_SIGNAL_CUSTOM_RANGE_DAYS} days or fewer.`
      : undefined;

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 960);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) setTempSelection(value);
  }, [open, value]);

  const setCustomRange = (from: Dayjs, to: Dayjs) => {
    const nextFrom = clampDayjsToDateRangeBounds(from, { maxDate });
    const nextTo = clampDayjsToDateRangeBounds(to, { maxDate });
    setTempSelection(
      toCustomSelection(
        nextFrom.isAfter(nextTo) ? nextTo : nextFrom,
        nextTo.isBefore(nextFrom) ? nextFrom : nextTo,
      ),
    );
  };

  const handleCalendarSelect = (selected?: CalendarDateRange) => {
    if (!selected?.from) return;
    const from = getTimezoneDayjs(getCalendarDateKey(selected.from));
    const to = getTimezoneDayjs(
      getCalendarDateKey(selected.to ?? selected.from),
    );
    setCustomRange(from, to);
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">
        {dateRange.startDate} - {dateRange.endDate}
      </span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          aria-label="Select date range"
          className={buttonVariants({ variant: 'outline', size: 'icon-sm' })}
        >
          <Calendar className="size-3.5" />
        </PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={8}
          className="w-max max-w-[calc(100vw-1rem)] p-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <DateRangePickerPanel
            computedDateRange={computedDateRange}
            isSmallScreen={isSmallScreen}
            maxDate={maxDate}
            onCalendarSelect={handleCalendarSelect}
            onCancel={() => {
              setTempSelection(value);
              setOpen(false);
            }}
            onEndDateChange={(to) =>
              setCustomRange(
                to.isBefore(computedDateRange.from)
                  ? to
                  : computedDateRange.from,
                to,
              )
            }
            onPresetSelect={(preset) => {
              const matchedPreset = DISPLAY_DURATION.find(
                (duration) => duration === preset,
              );
              if (matchedPreset) {
                setTempSelection({ type: 'preset', preset: matchedPreset });
              }
            }}
            onStartDateChange={(from) =>
              setCustomRange(
                from,
                from.isAfter(computedDateRange.to)
                  ? from
                  : computedDateRange.to,
              )
            }
            onUpdate={() => {
              if (validationMessage) return;
              onChange(tempSelection);
              setOpen(false);
            }}
            presetOptions={DISPLAY_DURATION.map((preset) => ({
              value: preset,
              label: preset,
            }))}
            selectedPresetValue={
              tempSelection.type === 'preset'
                ? tempSelection.preset
                : undefined
            }
            showPresets
            validationMessage={validationMessage}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const SignalDutationSelecter = SignalDurationSelector;
